<template>
  <div class="task-variant-groups" :class="{ 'task-variant-groups--dark': isDarkMode }">
    <div v-if="!groups.length" class="task-variant-empty">
      <v-icon size="32" color="grey">mdi-clipboard-text-outline</v-icon>
      <strong>Keine passenden Ämtli</strong>
      <span>Füge ein Ämtli aus dem Pool hinzu oder passe den Filter an.</span>
    </div>
    <v-expansion-panels v-else v-model="expanded" multiple flat>
      <v-expansion-panel v-for="group in groups" :key="group.id" class="task-variant-group" :dark="isDarkMode">
        <v-expansion-panel-header class="task-variant-header">
          <div class="task-variant-heading">
            <v-icon :color="group.color || 'primary'">{{ group.icon || 'mdi-clipboard-check-outline' }}</v-icon>
            <div>
              <strong>{{ group.title }}</strong>
              <span>{{ activeCount(group) }} aktiv · {{ group.variants.length }} {{ group.variants.length === 1 ? 'Variante' : 'Varianten' }}</span>
            </div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="task-variant-toolbar">
            <span>Für dieses Studio · jede Variante hat ihre eigene Planung</span>
            <v-btn v-if="group.supportsVariants" small outlined color="primary" :disabled="busy" class="text-none" @click="$emit('add', group.root)">
              <v-icon left small>mdi-plus</v-icon>{{ group.root.isVariantGroup ? 'Eigene Variante erstellen' : 'Variante hinzufügen' }}
            </v-btn>
          </div>
          <div v-for="variant in group.variants" :key="variant._id || variant.id" class="task-variant-row" :class="{ 'task-variant-row--inactive': !variant.enabledInStudio }">
            <div class="task-variant-copy">
              <div class="task-variant-name">
                <strong>{{ variant.variantName || (group.supportsVariants ? 'Standard' : group.title) }}</strong>
                <v-chip v-if="!variant.enabledInStudio" x-small outlined>Inaktiv</v-chip>
                <v-chip v-else-if="variant.readiness" x-small outlined :color="variant.readiness.color">{{ variant.readiness.label }}</v-chip>
              </div>
              <span>{{ variant.summary }}</span>
              <p v-if="variant.description">{{ variant.description }}</p>
              <span v-if="variant.checklist && variant.checklist.length" class="task-variant-checklist-count">
                <v-icon x-small>mdi-format-list-checks</v-icon> {{ variant.checklist.length }} Checklistenschritte
              </span>
            </div>
            <div class="task-variant-actions">
              <v-btn small outlined color="primary" :disabled="busy" class="text-none" @click="$emit('configure', variant)">Anpassen</v-btn>
              <v-btn v-if="group.supportsVariants" small text :disabled="busy" class="text-none" @click="$emit('duplicate', variant)">
                <v-icon left small>mdi-content-copy</v-icon>Duplizieren
              </v-btn>
              <v-btn small text :color="variant.enabledInStudio ? 'grey' : 'success'" :disabled="busy" class="text-none" @click="$emit('toggle', variant)">
                {{ variant.enabledInStudio ? 'Deaktivieren' : 'Aktivieren' }}
              </v-btn>
            </div>
          </div>
          <div v-if="week(group).some(day => day.entries.length)" class="task-variant-preview">
            <div class="task-variant-preview-title">Wochenübersicht <span>{{ weekLabel(group) }}</span></div>
            <div class="task-variant-week">
              <div v-for="day in week(group)" :key="day.date" class="task-variant-day">
                <strong>{{ day.label }} <span>{{ day.day }}.</span></strong>
                <span v-if="!day.entries.length" class="task-variant-no-entry">–</span>
                <span v-for="entry in day.entries" :key="entry.id" class="task-variant-day-entry" :class="{ 'task-variant-day-entry--flexible': entry.flexible }">
                  {{ entry.name }}<small v-if="entry.flexible">möglich</small>
                </span>
              </div>
            </div>
            <span class="task-variant-preview-note">Flexible Varianten stehen an möglichen Tagen. Die Automatik plant die eingestellte Häufigkeit.</span>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { taskVariantWeek } from "./taskVariantPresentation"

export default {
  name: "WorkCalendarTaskVariantGroups",
  props: {
    groups: { type: Array, default: () => [] },
    referenceDate: { type: String, required: true },
    isDarkMode: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  data: () => ({ expanded: [0] }),
  methods: {
    activeCount(group) { return group.variants.filter((variant) => variant.enabledInStudio).length },
    week(group) { return taskVariantWeek(group.variants, this.referenceDate) },
    weekLabel(group) {
      const days = this.week(group)
      return days.length ? `${days[0].date.split('-').reverse().join('.')} – ${days[6].date.split('-').reverse().join('.')}` : ""
    },
  },
}
</script>

<style scoped>
.task-variant-groups { --variant-border: #dfe6ee; --variant-muted: #637287; --variant-bg: #f4f7fa; color: #1e293b; }
.task-variant-groups--dark { --variant-border: #39414d; --variant-muted: #adb8c8; --variant-bg: #242c36; color: #edf2f7; }
.task-variant-group { margin-bottom: 12px; border: 1px solid var(--variant-border); border-radius: 14px !important; overflow: hidden; }
.task-variant-header { min-height: 78px; }
.task-variant-heading { display: flex; align-items: center; gap: 14px; }
.task-variant-heading strong { display: block; font-size: 17px; margin-bottom: 4px; }
.task-variant-heading span, .task-variant-toolbar > span { color: var(--variant-muted); font-size: 12px; }
.task-variant-toolbar { display: flex; gap: 12px; align-items: center; justify-content: space-between; flex-wrap: wrap; padding: 4px 0 16px; }
.task-variant-row { display: flex; justify-content: space-between; align-items: center; gap: 20px; padding: 18px 0; border-top: 1px solid var(--variant-border); }
.task-variant-copy { min-width: 0; flex: 1; }
.task-variant-name { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 5px; }
.task-variant-copy > span { font-size: 12px; color: var(--variant-muted); }
.task-variant-copy p { font-size: 13px; margin: 8px 0 4px; white-space: pre-line; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.task-variant-checklist-count { display: block; margin-top: 6px; }
.task-variant-actions { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.task-variant-actions .v-btn { min-height: 40px; }
.task-variant-row--inactive .task-variant-copy { color: var(--variant-muted); }
.task-variant-preview { border-top: 1px solid var(--variant-border); padding-top: 18px; }
.task-variant-preview-title { font-size: 13px; font-weight: 700; margin-bottom: 12px; }
.task-variant-preview-title span { font-size: 11px; color: var(--variant-muted); margin-left: 8px; font-weight: 400; }
.task-variant-week { display: grid; grid-template-columns: repeat(7, minmax(92px, 1fr)); gap: 6px; overflow-x: auto; padding-bottom: 8px; }
.task-variant-day { background: var(--variant-bg); padding: 10px 7px; border-radius: 8px; min-height: 90px; }
.task-variant-day > strong { display: block; font-size: 12px; margin-bottom: 10px; }
.task-variant-day > strong span { color: var(--variant-muted); font-weight: 400; }
.task-variant-day-entry { display: block; font-size: 11px; border-left: 3px solid #188650; background: rgba(24, 134, 80, .09); padding: 5px 6px; margin: 5px 0; overflow-wrap: anywhere; border-radius: 3px; }
.task-variant-day-entry--flexible { border-left-style: dotted; border-left-color: #72849c; background: rgba(114, 132, 156, .09); }
.task-variant-day-entry small { display: block; color: var(--variant-muted); font-size: 10px; }
.task-variant-no-entry, .task-variant-preview-note { color: var(--variant-muted); font-size: 11px; }
.task-variant-empty { display: flex; flex-direction: column; gap: 12px; align-items: center; padding: 40px 20px; text-align: center; }
.task-variant-empty span { color: var(--variant-muted); font-size: 13px; }
@media (max-width: 800px) { .task-variant-row { flex-direction: column; align-items: stretch; gap: 12px; } .task-variant-actions { justify-content: flex-start; } }
</style>
